import { Input, Stack } from '@chakra-ui/react'
import pick from 'lodash/pick'
import type { FC, FormEventHandler } from 'react'
import { useState } from 'react'

import { Button } from '@app/components/ui/button'
import { Field } from '@app/components/ui/field'
import { toaster } from '@app/components/ui/toaster'
import useStoreCurrentUser from '@app/hooks/useStoreCurrentUser'
import FormAlert from '@app/next/forms/formAlert'
import UrlInput from '@app/next/forms/urlInput'
import { CardHeader } from '@app/shared/layout'
import unpackErrors from '@app/utils/unpackErrors'
import { useCreateOrganizationMutation } from '@graphql/queries'
import type { OrganizationCreateInput } from '@graphql/types'

const OrganizationForm: FC = () => {
  const { user } = useStoreCurrentUser()
  const [errors, setErrors] = useState<ReturnType<typeof unpackErrors>>({})
  const [loading, setLoading] = useState(false)
  const [, createOrganization] = useCreateOrganizationMutation()

  const defaultHomepageValue = () => {
    if (!user?.email) {
      return ''
    }

    const emailDomain = user.email.split('@')[1]

    if (['gmail.com', 'hotmail.com', 'yahoo.com', 'aol.com', 'outlook.com'].includes(emailDomain)) {
      return ''
    }

    return `https://${emailDomain}`
  }

  const onSubmit: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault()

    const formData = new FormData(e.currentTarget)
    const input = Object.fromEntries(formData.entries())
    const validFields = ['name', 'homepageUrl']

    setLoading(true)
    try {
      const response = await createOrganization({ input: pick(input, validFields) as OrganizationCreateInput })

      if (response.error) {
        setErrors(unpackErrors([response.error]))
        return
      }

      const { errors: mutationErrors } = response.data.organizationCreate

      if (mutationErrors?.length) {
        setErrors(unpackErrors(mutationErrors))
        return
      }

      toaster.create({
        title: 'Creating your organization',
        type: 'success'
      })

      window.location.href = '/welcome'
    } catch (err) {
      setErrors(unpackErrors([err]))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Stack gap="6">
      <CardHeader title="Almost there!" subtitle="Add your organization details to finish setting up your account." />

      <form onSubmit={onSubmit} id="organization-form">
        <Stack gap="5">
          <FormAlert description={errors?.global?.message} title="Failed to create the organization!" />

          <Field label="Organization name" id="name" invalid={!!errors.name} required>
            <Input id="name" name="name" placeholder="Enter the name of your organization" />
          </Field>
          <Field
            label="Company website"
            id="homepageUrl"
            invalid={!!errors.homepageUrl}
            helperText="This helps us seed our AI and provide better suggestions."
            required
          >
            <UrlInput
              id="homepageUrl"
              defaultValue={defaultHomepageValue()}
              name="homepageUrl"
              placeholder="https://website.com"
            />
          </Field>
        </Stack>
      </form>
      <Stack gap={4}>
        <Button form="organization-form" loading={loading} size="md" type="submit">
          Continue
        </Button>
      </Stack>
    </Stack>
  )
}

export default OrganizationForm
